import React, { useContext, useEffect, useRef, useState } from 'react'

import Paginator from '../../components/ui/Paginator';

//import 'bootstrap-daterangepicker/daterangepicker.css';

import { Stock, StockMovement } from '../../models/interfaces';
import { useNavigate } from "react-router-dom";
import { StocksContext } from '../../context/stock/StockContext';
import topApi from '../../components/api/ToPApi';
import Message from '../../components/ui/Message';
import { StockMovementDialog, StockMovementFields } from './StockMovementDialog';
import { accountingSummary, initialInventoryButton, InventoryAccountingStatus } from './inventoryAccounting';
import { expirationConfig, expirationTitle, formatExpirationDate, getExpirationStatus } from './stockExpiration';
import { costingApi, CRC, formatCost, formatMoney } from './costing';
import { PageTour } from '../../components/ui/PageTour';
import { TourStep } from '../../components/ui/GuidedTour';

// Guía de la pantalla (se abre sola la primera vez; los elementos se marcan con data-tour)
const TOUR_STEPS: TourStep[] = [
    {
        key: 'intro', icon: 'bi-box-seam', title: 'Gestión de inventario',
        content: 'Aquí controla cuántas unidades tiene de cada producto, cuánto le cuestan y cada entrada o salida. Las ventas descuentan el stock automáticamente.',
    },
    {
        key: 'add', target: '[data-tour="stock-add"]', icon: 'bi-plus-lg', title: '1. Agregue productos al inventario',
        content: 'Elija un producto y registre su cantidad inicial, costo, unidad de medida, proveedor y el mínimo para la alerta de stock bajo.',
    },
    {
        key: 'purchases', target: '[data-tour="stock-purchases"]', icon: 'bi-cart-plus', title: '2. Registre sus compras',
        content: 'Cada compra a un proveedor suma unidades y actualiza el costo promedio del producto.',
    },
    {
        key: 'options', target: '[data-tour="stock-options"]', icon: 'bi-three-dots-vertical', title: 'Más opciones',
        content: 'Traslados entre sucursales, conteo físico, categorías, configuración del inventario y exportar la lista. Aquí también está "Guía de esta pantalla" para repetir esta guía.',
    },
    {
        key: 'stats', target: '[data-tour="stock-stats"]', icon: 'bi-speedometer2', title: 'Resumen',
        content: 'De un vistazo: cuántos productos tiene, el valor del inventario y cuántos están en stock bajo o agotados.',
    },
    {
        key: 'filters', target: '[data-tour="stock-filters"]', icon: 'bi-funnel', title: 'Buscar y filtrar',
        content: 'Filtre por categoría con las pestañas, busque por nombre o código, y vea solo los productos con stock bajo, agotados o en negativo.',
    },
    {
        key: 'table', target: '[data-tour="stock-table"]', icon: 'bi-arrow-left-right', title: 'Ajustar cantidades',
        content: 'En cada fila puede reemplazar, agregar o sacar unidades. El botón de la libreta muestra el kardex: todos los movimientos del producto con su saldo y costo.',
    },
    {
        key: 'side', target: '[data-tour="stock-side"]', icon: 'bi-bell', title: 'Movimientos y alertas',
        content: 'Revise los últimos movimientos y los productos que están por agotarse para reponerlos a tiempo.',
    },
];


const PAGE_SIZE = 10;

const statusConfig: Record<string, { label: string; badgeClass: string }> = {
    in_stock: { label: 'En stock', badgeClass: 'bg-success-subtle text-success-emphasis' },
    low_stock: { label: 'Stock bajo', badgeClass: 'bg-warning-subtle text-warning-emphasis' },
    out_of_stock: { label: 'Agotado', badgeClass: 'bg-danger-subtle text-danger-emphasis' },
    negative: { label: 'En negativo', badgeClass: 'bg-danger text-white' },
};

const getStatus = (stock: Stock) => {
    if (stock.status) return stock.status;
    if (stock.stock < 0) return 'negative';
    if (stock.stock <= 0) return 'out_of_stock';
    if (stock.stock <= stock.minimunStock) return 'low_stock';
    return 'in_stock';
};

export const StocksPage = () => {
    const [buscar, setBuscar] = useState('');
    const [pageActual, setPageActual] = useState<number>(1);
    const [activeCategory, setActiveCategory] = useState<number | string>('all');
    const [statusFilter, setStatusFilter] = useState<string>('all');

    const {
        stocks,
        totalPage,
        totalRecords,
        stats,
        categories,
        isLoading,
        loadStocks,
        deleteStock,
        addStock,
        removeStock,
        replaceStock,
        exportStocks,
        loadRecentMovements,
        applyInventoryToProducts,
        loadAccountingStatus,
        sendInitialInventory,
        retryAccountingErrors
    } = useContext(StocksContext);

    const navigate = useNavigate();

    // Fila en modo "reemplazar stock" (lápiz) o "agregar stock" (+)
    const [editingId, setEditingId] = useState<number | null>(null);
    const [addingId, setAddingId] = useState<number | null>(null);
    const [inlineValue, setInlineValue] = useState<string>('');
    const [inlineSaving, setInlineSaving] = useState(false);

    const [lowStockAlerts, setLowStockAlerts] = useState<Stock[]>([]);
    const [recentMovements, setRecentMovements] = useState<StockMovement[]>([]);
    const [isExporting, setIsExporting] = useState(false);
    const [applyConfirm, setApplyConfirm] = useState(false);
    const [isApplying, setIsApplying] = useState(false);
    const [isRefreshing, setIsRefreshing] = useState(false);

    // Integración con la contabilidad: con ella activa, los cambios de stock piden tipo y motivo
    // undefined mientras se consulta; null si no se pudo consultar
    const [accountingStatus, setAccountingStatus] = useState<InventoryAccountingStatus | null | undefined>(undefined);
    const [initialConfirm, setInitialConfirm] = useState(false);
    const [isSendingInitial, setIsSendingInitial] = useState(false);
    const [movementDialog, setMovementDialog] = useState<{ mode: 'add' | 'remove' | 'replace'; stock: Stock; quantity: number } | null>(null);
    // Fila en modo "sacar stock" (−) y moneda del comercio (el costo promedio está en esa moneda)
    const [removingId, setRemovingId] = useState<number | null>(null);
    const [companyCurrency, setCompanyCurrency] = useState<number>(CRC);

    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [confirm, setConfirm] = useState('');
    const [stockId, setStockId] = useState<number>();

    // Recarga cuando cambia la página, categoría o estado
    useEffect(() => {
        loadStocks(pageActual - 1, PAGE_SIZE, { search: buscar, category: activeCategory, status: statusFilter });
    }, [pageActual, activeCategory, statusFilter])

    // Búsqueda con debounce
    useEffect(() => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
            if (pageActual !== 1) {
                setPageActual(1);
            } else {
                loadStocks(0, PAGE_SIZE, { search: buscar, category: activeCategory, status: statusFilter });
            }
        }, 400);
        return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
    }, [buscar])

    useEffect(() => {
        loadLowStockAlerts();
        loadMovements();
        loadAccountingStatus().then(setAccountingStatus);
        costingApi.configuration().then((r) => { if (r.data?.currency) setCompanyCurrency(Number(r.data.currency)); });
    }, [])

    const loadLowStockAlerts = async () => {
        try {
            const resp = await topApi.get('/stock/getStocks', { params: { page: 1, pageSize: 5, status: 'low_stock' } });
            setLowStockAlerts(resp.data.stocks ?? []);
        } catch (e) {
            setLowStockAlerts([]);
        }
    }

    const loadMovements = async () => {
        const movements = await loadRecentMovements(5);
        setRecentMovements(movements);
    }

    // Vuelve a consultar los datos de la pantalla conservando búsqueda, filtros y página
    const handleRefresh = async () => {
        setIsRefreshing(true);
        try {
            await Promise.all([
                loadStocks(pageActual - 1, PAGE_SIZE, { search: buscar, category: activeCategory, status: statusFilter }),
                loadLowStockAlerts(),
                loadMovements(),
                loadAccountingStatus().then(setAccountingStatus),
            ]);
        } finally {
            setIsRefreshing(false);
        }
    }

    const handleNew = () => {
        navigate("/stock");
    }

    const handleEdit = (stock: Stock) => {
        navigate("/stock", { state: { stock } });
    }



    const handleDelete = async (stock_id: number) => {
        setStockId(stock_id)
        setConfirm('¿Está seguro que desea eliminar este producto del inventario?');
    }

    const processDelete = async () => {
        setConfirm('')
        const resp = await deleteStock(stockId);

        if (resp !== "success") {
            setError(resp);
        } else {
            loadLowStockAlerts();
        }
        setStockId(-1);
    }

    const startEdit = (stock: Stock) => {
        setAddingId(null);
        setRemovingId(null);
        setEditingId(stock.id);
        setInlineValue(String(stock.stock));
    }

    const startAdd = (stock: Stock) => {
        setEditingId(null);
        setRemovingId(null);
        setAddingId(stock.id);
        setInlineValue('');
    }

    const startRemove = (stock: Stock) => {
        setEditingId(null);
        setAddingId(null);
        setRemovingId(stock.id);
        setInlineValue('');
    }

    const cancelInline = () => {
        setEditingId(null);
        setAddingId(null);
        setRemovingId(null);
        setInlineValue('');
    }

    const saveMovement = async (mode: 'add' | 'remove' | 'replace', stock: Stock, value: number, fields: StockMovementFields) => {
        setInlineSaving(true);
        const resp = mode === 'add'
            ? await addStock({ id: stock.id, addStock: value, ...fields })
            : mode === 'remove'
                ? await removeStock({ id: stock.id, removeStock: value, ...fields })
                : await replaceStock({ id: stock.id, newStock: value, ...fields });
        setInlineSaving(false);
        if (resp !== 'success') {
            setError(resp);
        } else {
            setMovementDialog(null);
            cancelInline();
            loadLowStockAlerts();
            loadMovements();
            if (accountingStatus?.active) loadAccountingStatus().then(setAccountingStatus);
        }
    }

    const confirmReplace = async (stock: Stock) => {
        const value = parseFloat(inlineValue);
        if (isNaN(value) || value < 0) {
            setError('Ingrese una cantidad válida');
            return;
        }
        if (value === Number(stock.stock)) {
            cancelInline();
            return;
        }
        setMovementDialog({ mode: 'replace', stock, quantity: value });
    }

    // "+" y "−": la cantidad se confirma en el diálogo con su motivo (y el tipo de salida)
    const confirmAddOrRemove = (stock: Stock, mode: 'add' | 'remove') => {
        const value = parseFloat(inlineValue);
        if (isNaN(value) || value <= 0) {
            setError('Ingrese una cantidad válida');
            return;
        }
        setMovementDialog({ mode, stock, quantity: value });
    }

    // Botón "Enviar inventario inicial a contabilidad" (una sola vez)
    const processSendInitial = async () => {
        setInitialConfirm(false);
        setIsSendingInitial(true);
        const resp = await sendInitialInventory();
        setIsSendingInitial(false);
        if (resp.error) {
            setError(resp.error);
            return;
        }
        if (resp.status) setAccountingStatus(resp.status);
        setSuccess(resp.products
            ? `Se registró el inventario inicial de ${resp.products} producto(s) en ${resp.movements} parte(s). Se enviará a la contabilidad en los próximos minutos y desde ahora también los movimientos de inventario.`
            : 'No hay productos con stock: se activó el envío de los movimientos de inventario a la contabilidad.');
    }

    const processRetryAccounting = async () => {
        const resp = await retryAccountingErrors();
        if (resp.error) {
            setError(resp.error);
            return;
        }
        if (resp.status) setAccountingStatus(resp.status);
        setSuccess(`Se volverán a enviar ${resp.retried ?? 0} movimiento(s) a la contabilidad.`);
    }

    // Botón "Aplicar inventario": pasa el stock actual a la cantidad de los productos asociados
    const processApplyInventory = async () => {
        setApplyConfirm(false);
        setIsApplying(true);
        const resp = await applyInventoryToProducts();
        setIsApplying(false);
        if (resp.error) {
            setError(resp.error);
        } else if (resp.associatedProducts === 0) {
            setSuccess('No hay productos asociados al inventario para actualizar.');
        } else if (resp.updatedProducts === 0) {
            setSuccess('Las cantidades de los productos ya coinciden con el inventario. No hubo cambios.');
        } else if (resp.updatedProducts === resp.associatedProducts) {
            setSuccess(`Se actualizó la cantidad de ${resp.updatedProducts} producto(s) con el inventario actual.`);
        } else {
            setSuccess(`Se actualizó la cantidad de ${resp.updatedProducts} de ${resp.associatedProducts} producto(s) asociados; el resto ya coincidía con el inventario.`);
        }
    }

    const handleExport = async () => {
        setIsExporting(true);
        try {
            const data = await exportStocks({ search: buscar, category: activeCategory, status: statusFilter });
            if (!data || data.length === 0) {
                setError('No hay productos para exportar');
                return;
            }
            const rows = data.map((s) => ({
                'Código': s.code,
                'Nombre': s.name,
                'Categoría': s.category?.name ?? '',
                'Unidad': s.unit,
                'Cantidad en stock': s.stock,
                'Cantidad mínima': s.minimunStock,
                'Proveedor': s.supplier?.name ?? '',
                'Estado': statusConfig[getStatus(s)].label,
                'Fecha de vencimiento': formatExpirationDate(s.expirationDate),
                'Vencimiento': (() => { const e = getExpirationStatus(s); return e ? expirationConfig[e].label : ''; })(),
            }));
            const headers = Object.keys(rows[0]);
            const csvRows = [
                headers.join(','),
                ...rows.map(row => headers.map(field => `"${String(row[field] ?? '').replace(/"/g, '""')}"`).join(','))
            ];
            const csvContent = csvRows.join('\n');
            const blob = new Blob(['﻿' + csvContent], { type: 'text/csv;charset=utf-8;' });
            const link = document.createElement('a');
            link.href = URL.createObjectURL(blob);
            link.setAttribute('download', `inventario_${new Date().toISOString().slice(0, 10)}.csv`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } finally {
            setIsExporting(false);
        }
    }

    const statCards = [
        { key: 'total', label: 'Total de productos', value: stats.total, icon: 'bi-box-seam', color: '#0d6efd', bg: '#e7f1ff' },
        { key: 'value', label: 'Valor del inventario', value: formatMoney(stats.totalValue ?? 0, companyCurrency), icon: 'bi-cash-stack', color: '#6f42c1', bg: '#f0ebfa' },
        { key: 'inStock', label: 'En stock', value: stats.inStock, icon: 'bi-check-circle', color: '#198754', bg: '#e8f6ee' },
        { key: 'lowStock', label: 'Stock bajo', value: stats.lowStock, icon: 'bi-exclamation-triangle', color: '#fd7e14', bg: '#fff2e6' },
        { key: 'outOfStock', label: 'Agotado', value: stats.outOfStock, icon: 'bi-x-circle', color: '#dc3545', bg: '#fdeaec' },
    ];

    const initialButton = initialInventoryButton(accountingStatus);

    const startIndex = totalRecords === 0 ? 0 : (pageActual - 1) * PAGE_SIZE + 1;
    const endIndex = Math.min(pageActual * PAGE_SIZE, totalRecords);

    const removeLocalError = () => setError('');
    const removeConfirmMessage = () => setConfirm('');

    const removeSuccessMessage = () => setSuccess('');;

    return (

        <div className='mt-5 container-fluid px-4' style={styles.container}>

            <div className="d-flex flex-wrap justify-content-between align-items-start mb-4">
                <div>
                    <h3 className="mb-1">Gestión de Inventario</h3>
                    <p className="text-muted mb-0">Controle los niveles de stock y gestione sus productos.</p>
                </div>
                <div className="d-flex flex-wrap gap-2 mt-2 mt-md-0">
                    <button type="button" className="btn btn-outline-secondary" onClick={handleRefresh} disabled={isRefreshing} title="Volver a consultar los datos de esta pantalla">
                        {isRefreshing
                            ? <><span className="spinner-border spinner-border-sm me-1" /> Actualizando...</>
                            : <><i className="bi bi-arrow-clockwise me-1" /> Actualizar</>}
                    </button>
                    {/* Acciones secundarias agrupadas; "Agregar Producto" queda siempre visible */}
                    <div className="dropdown" data-tour="stock-options">
                        <button type="button" className="btn btn-outline-secondary dropdown-toggle" data-bs-toggle="dropdown" aria-expanded="false"
                            disabled={isSendingInitial || isApplying || isExporting}>
                            {isSendingInitial || isApplying || isExporting
                                ? <><span className="spinner-border spinner-border-sm me-1" /> {isSendingInitial ? 'Enviando...' : isApplying ? 'Aplicando...' : 'Exportando...'}</>
                                : <><i className="bi bi-three-dots-vertical me-1" /> Opciones</>}
                        </button>
                        <ul className="dropdown-menu dropdown-menu-end shadow-sm">
                            <li><PageTour id="stocks" steps={TOUR_STEPS} variant="dropdown-item" /></li>
                            <li><hr className="dropdown-divider" /></li>
                            <li>
                                <button type="button" className="dropdown-item" onClick={() => navigate('/stock-transfers')}>
                                    <i className="bi bi-truck me-2" /> Traslados entre sucursales
                                </button>
                            </li>
                            <li>
                                <button type="button" className="dropdown-item" onClick={() => navigate('/stock-count')}>
                                    <i className="bi bi-clipboard-check me-2" /> Conteo físico
                                </button>
                            </li>
                            <li>
                                <button type="button" className="dropdown-item" onClick={() => navigate('/stock-settings')}>
                                    <i className="bi bi-gear me-2" /> Configuración del inventario
                                </button>
                            </li>
                            <li><hr className="dropdown-divider" /></li>
                            <li>
                                <button type="button" className="dropdown-item" onClick={() => navigate('/categories')}>
                                    <i className="bi bi-tags me-2" /> Categorías
                                </button>
                            </li>
                            <li>
                                <button type="button" className="dropdown-item" onClick={() => setApplyConfirm(true)}>
                                    <i className="bi bi-arrow-repeat me-2" /> Aplicar inventario
                                </button>
                            </li>
                            <li>
                                <button type="button" className="dropdown-item" onClick={handleExport}>
                                    <i className="bi bi-upload me-2" /> Exportar
                                </button>
                            </li>
                            {initialButton && (
                                <>
                                    <li><hr className="dropdown-divider" /></li>
                                    <li title={initialButton.hint ?? undefined}>
                                        <button type="button" className="dropdown-item" onClick={() => setInitialConfirm(true)} disabled={!initialButton.enabled}>
                                            <i className="bi bi-journal-arrow-up me-2" /> Enviar inventario inicial a contabilidad
                                        </button>
                                    </li>
                                </>
                            )}
                        </ul>
                    </div>
                    <button type="button" className="btn btn-outline-primary" data-tour="stock-purchases" onClick={() => navigate('/stock-purchases')}>
                        <i className="bi bi-cart-plus me-1" /> Compras
                    </button>
                    <button type="button" className="btn btn-primary" data-tour="stock-add" onClick={handleNew}>
                        <i className="bi bi-plus-lg me-1" /> Agregar Producto
                    </button>
                </div>
            </div>

            {initialButton?.hint && (
                <div className="d-flex align-items-center gap-2 mb-3 small text-muted">
                    <i className="bi bi-info-circle" />
                    <span>{initialButton.hint}</span>
                </div>
            )}

            {accountingStatus?.active && (
                <div className="d-flex align-items-center gap-2 mb-3 small text-muted">
                    <i className="bi bi-journal-check" />
                    <span>{accountingSummary(accountingStatus)}</span>
                    {accountingStatus.errors > 0 && (
                        <>
                            <button type="button" className="btn btn-link btn-sm p-0" onClick={() => navigate('/stock-movements')}>Ver movimientos</button>
                            <button type="button" className="btn btn-link btn-sm p-0" onClick={processRetryAccounting}>Reintentar</button>
                        </>
                    )}
                </div>
            )}

            {(stats.negative ?? 0) > 0 && (
                <div className="alert alert-danger d-flex align-items-center gap-2 py-2">
                    <i className="bi bi-exclamation-octagon" />
                    <span>{stats.negative} producto(s) con existencia negativa: se vendió más de lo registrado. Revise sus compras o haga un conteo físico.</span>
                    <button type="button" className="btn btn-link btn-sm p-0 ms-auto" onClick={() => { setStatusFilter('negative'); setPageActual(1); }}>Ver productos</button>
                </div>
            )}

            {/* Tarjetas de resumen */}
            <div className="row g-3 mb-4" data-tour="stock-stats">
                {statCards.map(card => (
                    <div className="col-6 col-md" key={card.key}>
                        <div className="card border-0 shadow-sm h-100">
                            <div className="card-body d-flex align-items-center gap-3">
                                <div style={{ ...styles.iconCircle, backgroundColor: card.bg, color: card.color }}>
                                    <i className={`bi ${card.icon}`} />
                                </div>
                                <div>
                                    <div className="text-muted small">{card.label}</div>
                                    <div className="fs-4 fw-semibold">{card.value}</div>
                                </div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            <div className="row g-3">
                <div className="col-12 col-lg-9">
                    <div className="card border-0 shadow-sm">
                        <div className="card-body">

                            <div data-tour="stock-filters">
                            {/* Tabs de categoría */}
                            <ul className="nav nav-tabs mb-3">
                                <li className="nav-item">
                                    <button
                                        className={`nav-link ${activeCategory === 'all' ? 'active' : ''}`}
                                        onClick={() => { setActiveCategory('all'); setPageActual(1); }}
                                    >
                                        Todos ({totalRecords})
                                    </button>
                                </li>
                                {categories.map(cat => (
                                    <li className="nav-item" key={cat.id}>
                                        <button
                                            className={`nav-link ${activeCategory === cat.id ? 'active' : ''}`}
                                            onClick={() => { setActiveCategory(cat.id); setPageActual(1); }}
                                        >
                                            {cat.name} ({cat.count})
                                        </button>
                                    </li>
                                ))}
                            </ul>

                            {/* Búsqueda y filtro de estado */}
                            <div className="row g-2 mb-3">
                                <div className="col-md-8">
                                    <div className="input-group">
                                        <span className="input-group-text bg-white"><i className="bi bi-search" /></span>
                                        <input
                                            className="form-control"
                                            id="search"
                                            type="text"
                                            placeholder="Buscar por nombre, código o categoría..."
                                            value={buscar}
                                            onChange={(e) => setBuscar(e.target.value)}
                                        />
                                    </div>
                                </div>
                                <div className="col-md-4">
                                    <select
                                        className="form-select"
                                        value={statusFilter}
                                        onChange={(e) => { setStatusFilter(e.target.value); setPageActual(1); }}
                                    >
                                        <option value="all">Todos los estados</option>
                                        <option value="in_stock">En stock</option>
                                        <option value="low_stock">Stock bajo</option>
                                        <option value="out_of_stock">Agotado</option>
                                        <option value="negative">En negativo</option>
                                    </select>
                                </div>
                            </div>
                            </div>

                            <div className="table-responsive" data-tour="stock-table">
                                <table id='stockTable' className="table table-hover align-middle">
                                    <thead>
                                        <tr className="text-muted small text-uppercase">
                                            <th>Código</th>
                                            <th>Nombre</th>
                                            <th>Categoría</th>
                                            <th style={{ minWidth: 190 }}>Cantidad en stock</th>
                                            <th>Unidad</th>
                                            <th>Costo promedio</th>
                                            <th>Proveedor</th>
                                            <th>Estado</th>
                                            <th style={{ textAlign: 'center' }}>Acciones</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {isLoading ? (
                                            <tr><td colSpan={9} className='text-center py-4'>
                                                <span className="spinner-border spinner-border-sm me-2" /> Cargando...
                                            </td></tr>
                                        ) : stocks.length > 0 ? (
                                            stocks.map(stock => {
                                                const status = getStatus(stock);
                                                const cfg = statusConfig[status];
                                                const expiration = getExpirationStatus(stock);
                                                const isEditingRow = editingId === stock.id;
                                                const isAddingRow = addingId === stock.id;
                                                const isRemovingRow = removingId === stock.id;
                                                return (
                                                    <tr key={stock.id}>
                                                        <td className="text-muted">{stock.code}</td>
                                                        <td className="fw-medium">{stock.name}</td>
                                                        <td>{stock.category?.name ?? '-'}</td>
                                                        <td width={235}>
                                                            {isEditingRow || isAddingRow || isRemovingRow ? (
                                                                <div className="d-flex align-items-center gap-1 w-100">
                                                                    <div className='flex-grow-1'>
                                                                        <input
                                                                            type="number"
                                                                            className="form-control form-control-sm"
                                                                            style={{ width: '150px' }}
                                                                            autoFocus
                                                                            placeholder={isAddingRow || isRemovingRow ? 'Cantidad' : undefined}
                                                                            aria-label={isRemovingRow ? `Cantidad a sacar de ${stock.name}` : isAddingRow ? `Cantidad a agregar a ${stock.name}` : `Cantidad contada de ${stock.name}`}
                                                                            value={inlineValue}
                                                                            onChange={(e) => setInlineValue(e.target.value)}
                                                                            disabled={inlineSaving}
                                                                        />
                                                                    </div>
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-sm btn-success"
                                                                        disabled={inlineSaving}
                                                                        title="Continuar"
                                                                        onClick={() => isEditingRow ? confirmReplace(stock) : confirmAddOrRemove(stock, isRemovingRow ? 'remove' : 'add')}
                                                                    >
                                                                        <i className="bi bi-check-lg" />
                                                                    </button>
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-sm btn-outline-secondary"
                                                                        disabled={inlineSaving}
                                                                        onClick={cancelInline}
                                                                    >
                                                                        <i className="bi bi-x-lg" />
                                                                    </button>
                                                                </div>
                                                            ) : (
                                                                <div className="d-flex align-items-center gap-2 w-100">
                                                                    <span className={`fw-semibold flex-grow-1 ${stock.stock < 0 ? 'text-danger' : ''}`}>{stock.stock}</span>

                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-sm btn-outline-secondary"
                                                                        title="Reemplazar cantidad"
                                                                        onClick={() => startEdit(stock)}
                                                                    >
                                                                        <i className="bi bi-pencil" />
                                                                    </button>

                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-sm btn-outline-secondary"
                                                                        title="Agregar cantidad"
                                                                        onClick={() => startAdd(stock)}
                                                                    >
                                                                        <i className="bi bi-plus-lg" />
                                                                    </button>

                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-sm btn-outline-secondary"
                                                                        title="Sacar cantidad"
                                                                        onClick={() => startRemove(stock)}
                                                                    >
                                                                        <i className="bi bi-dash-lg" />
                                                                    </button>
                                                                </div>
                                                            )}
                                                        </td>
                                                        <td>{stock.unit}</td>
                                                        <td title={`Valor del inventario: ${formatMoney(stock.inventoryValue ?? 0, companyCurrency)}`}>
                                                            {stock.averageCost === null || stock.averageCost === undefined ? '-' : formatCost(stock.averageCost, companyCurrency)}
                                                        </td>
                                                        <td>{stock.supplier?.name ?? '-'}</td>
                                                        <td>
                                                            <div className="d-flex flex-wrap gap-1">
                                                                <span className={`badge rounded-pill ${cfg.badgeClass}`}>{cfg.label}</span>
                                                                {expiration && (
                                                                    <span className={`badge rounded-pill ${expirationConfig[expiration].badgeClass}`} title={expirationTitle(stock)}>
                                                                        <i className={`bi ${expiration === 'expired' ? 'bi-x-circle' : 'bi-hourglass-split'} me-1`} />
                                                                        {expirationConfig[expiration].label}
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </td>
                                                        <td style={{ textAlign: 'center', width: 150 }} className="text-nowrap">
                                                            <button
                                                                type="button"
                                                                className="btn btn-sm btn-outline-secondary me-1"
                                                                title="Kardex"
                                                                onClick={() => navigate(`/stock-kardex?id=${stock.id}`)}
                                                            >
                                                                <i className="bi bi-journal-text" />
                                                            </button>
                                                            <button
                                                                type="button"
                                                                className="btn btn-sm btn-primary"
                                                                title="Editar producto"
                                                                onClick={() => handleEdit(stock)}
                                                            >
                                                                <i className="bi bi-pencil" />
                                                            </button>
                                                            <button
                                                                type="button"
                                                                className="btn btn-sm btn-danger ms-1"
                                                                title="Eliminar producto"
                                                                onClick={() => handleDelete(stock.id)}
                                                            >
                                                                <i className="bi bi-trash" />
                                                            </button>
                                                        </td>
                                                    </tr>
                                                )
                                            })
                                        ) : (
                                            <tr><td colSpan={9} className='text-center py-4 text-muted'>No hay productos en inventario.</td></tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>

                            <div className="d-flex justify-content-between align-items-center mt-2 flex-wrap gap-2">
                                <span className="text-muted small">
                                    {totalRecords > 0 ? `Mostrando ${startIndex}-${endIndex} de ${totalRecords} productos` : ''}
                                </span>
                                {totalPage > 1 ? <Paginator totalPage={totalPage} pageActual={pageActual} onChange={(actual) => setPageActual(actual)} /> : <div />}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Panel lateral */}
                <div className="col-12 col-lg-3" data-tour="stock-side">
                    <div className="card border-0 shadow-sm mb-3">
                        <div className="card-body">
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <h6 className="mb-0">Movimientos recientes</h6>
                                <button type="button" className="btn btn-link btn-sm p-0 text-decoration-none" onClick={() => navigate('/stock-movements')}>
                                    Ver todos <i className="bi bi-chevron-right" />
                                </button>
                            </div>
                            {recentMovements.length === 0 ? (
                                <p className="text-muted small mb-0">Sin movimientos recientes.</p>
                            ) : (
                                <ul className="list-unstyled mb-0">
                                    {recentMovements.map(mv => (
                                        <li key={mv.id} className="d-flex justify-content-between mb-2 small">
                                            <div>
                                                <div className="fw-medium">{mv.productName ?? `Producto #${mv.inventoryProductId}`}</div>
                                                <div className="text-muted">{mv.movementType} · {mv.updateDate ? new Date(mv.updateDate).toLocaleString() : ''}</div>
                                            </div>
                                            <span className={mv.newStock >= mv.previousStock ? 'text-success fw-semibold' : 'text-danger fw-semibold'}>
                                                {mv.newStock >= mv.previousStock ? '+' : '-'}{Math.abs(mv.newStock - mv.previousStock)}
                                            </span>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    </div>

                    <div className="card border-0 shadow-sm">
                        <div className="card-body">
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <h6 className="mb-0">Alertas de stock bajo</h6>
                                <button type="button" className="btn btn-link btn-sm p-0 text-decoration-none" onClick={() => navigate('/low-stock')}>
                                    Ver todos <i className="bi bi-chevron-right" />
                                </button>
                            </div>
                            {lowStockAlerts.length === 0 ? (
                                <p className="text-muted small mb-0">No hay productos con stock bajo.</p>
                            ) : (
                                <ul className="list-unstyled mb-0">
                                    {lowStockAlerts.map(item => (
                                        <li key={item.id} className="d-flex justify-content-between align-items-center mb-2">
                                            <span className="small">{item.name}</span>
                                            <span className="badge bg-warning-subtle text-warning-emphasis">{item.stock} {item.unit}</span>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <div style={{ height: '60px' }}></div>

            {/* Mensajes locales */}
            {
                error.length > 0 && (
                    <Message
                        type="error"
                        message={error}
                        closeDialog={removeLocalError}
                        handleOk={removeLocalError}
                        backgroundColor="rgba(0,0,0,0.4)"
                    />
                )
            }

            {
                success.length > 0 && (
                    <Message
                        type="success"
                        title="Exitoso"
                        message={success}
                        closeDialog={removeSuccessMessage}
                        handleOk={removeSuccessMessage}
                        backgroundColor="rgba(0,0,0,0.4)"
                    />
                )
            }

            {
                applyConfirm && (
                    <Message
                        type="success"
                        title="Aplicar inventario"
                        message="Se actualizará la cantidad de todos los productos del menú asociados al inventario con el stock actual de cada producto. ¿Desea continuar?"
                        closeDialog={() => setApplyConfirm(false)}
                        showCancel={true}
                        handleCancel={() => setApplyConfirm(false)}
                        handleOk={processApplyInventory}
                        backgroundColor="rgba(0,0,0,0.4)"
                    />
                )
            }

            {
                initialConfirm && (
                    <Message
                        type="info"
                        title="Inventario inicial"
                        message="Se enviará a la contabilidad el stock actual de todos los productos, con su costo, como inventario inicial. Se hace una sola vez: antes, el contador debe tener configuradas las cuentas por categoría y la cuenta del inventario inicial, y sin el costo de ventas en la plantilla de la factura de facturación. Desde ese momento se enviarán también las compras, ventas y ajustes. ¿Desea continuar?"
                        closeDialog={() => setInitialConfirm(false)}
                        showCancel={true}
                        handleCancel={() => setInitialConfirm(false)}
                        handleOk={processSendInitial}
                        okText="Enviar"
                        backgroundColor="rgba(0,0,0,0.4)"
                    />
                )
            }

            {
                movementDialog && (
                    <StockMovementDialog
                        mode={movementDialog.mode}
                        productName={movementDialog.stock.name}
                        quantity={movementDialog.quantity}
                        previous={movementDialog.mode === 'replace' ? Number(movementDialog.stock.stock) : undefined}
                        averageCost={movementDialog.stock.averageCost === null || movementDialog.stock.averageCost === undefined ? null : Number(movementDialog.stock.averageCost)}
                        saving={inlineSaving}
                        onClose={() => setMovementDialog(null)}
                        onConfirm={(fields) => saveMovement(movementDialog.mode, movementDialog.stock, movementDialog.quantity, fields)}
                    />
                )
            }

            {
                confirm.length > 0 && (
                    <Message
                        type="success"
                        title="Atención"
                        message={confirm}
                        closeDialog={removeConfirmMessage}
                        showCancel={true}
                        handleCancel={removeConfirmMessage}
                        handleOk={processDelete}
                        backgroundColor="rgba(0,0,0,0.4)"
                    />
                )
            }

        </div>
    )
}


const styles = {
    container: {
        flex: 1,
        marginTop: 10,
    },
    iconCircle: {
        width: 48,
        height: 48,
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 20,
        flexShrink: 0,
    }
};
